"use client";
import { PortableText, type PortableTextComponents, type PortableTextMarkComponentProps, type PortableTextTypeComponent } from '@portabletext/react';
import { type ArbitraryTypedObject, type PortableTextBlock } from '@portabletext/types';
import styles from "./RichText.module.css";

import getYouTubeID from 'get-youtube-id';

import Facebook from "@/components/framework/richtext/some/Facebook";
import Instagram from "@/components/framework/richtext/some/Instagram";
import TikTok from "@/components/framework/richtext/some/TikTok";
import Twitter from "@/components/framework/richtext/some/Twitter";
import Table from "@/components/framework/richtext/Table";
import CMSImage from "@/components/image/CMSImage";
import { getDocumentUrlBySlug } from "@/lib/getDocumentUrl";
import { type Slug } from "@/sanity/sanity.types";
import { type BygdekinoenModifiedImageDoument } from "@/sanity/types/DeRefImageField";
import { type TypedObject } from "@sanity/types";
import classnames from "classnames";
import Link from 'next/link';
import YT from 'react-youtube';
import { BygdekinoenModifiedFileDoument } from "@/sanity/types/DeRefFileField";
import { MdFileDownload } from "react-icons/md";
import classNames from "classnames";

//#region [Props]
type YouTubeProps = {
    value: {
        _key: string;
        _type: string;
        url: string
    }
}
//#endregion

//#region [Component]
const YouTube = ({ value }: YouTubeProps) => {
    const { url } = value;
    if (!url) {
        return null;
    }

    const ytId = getYouTubeID(url);
    if (!ytId) {
        return null;
    }

    return <YT videoId={ytId} className={styles.yt} />
};
//#endregion


//#region [Props]
type InlineImageProps = {
    value: {
        placement: "alignLeft" | "fullWidth" | "alignRight" | "alignRightAligned" | "alignLeftAligned";
        title: string;
        imageRef: BygdekinoenModifiedImageDoument;
    }
};

type InlineFileProps = {
    value: {
        fileRef: BygdekinoenModifiedFileDoument;
    }
}
//#endregion

//#region [Component]
const InlineImage = ({ value }: InlineImageProps) => {
    const { placement, imageRef, title } = value;

    switch (placement) {
        case "alignLeft":
            return <div className={styles.leftAlignedImage}><CMSImage className={styles.alignedImage} image={imageRef} altText={title} sizes="(min-width: 641px) 600px, 100vw" width={600} /></div>;
        case "alignRight":
            return <div className={styles.rightAlignedImage}><CMSImage className={styles.alignedImage} image={imageRef} altText={title} sizes="(min-width: 641px) 600px, 100vw" width={600} /></div>;
        case "alignLeftAligned":
            return <div className={classNames(styles.leftAlignedImage, styles.inlineWithText)}><CMSImage className={styles.alignedImage} image={imageRef} altText={title} sizes="(min-width: 641px) 300px, 100vw" width={300} /></div>;
        case "alignRightAligned":
            return <div className={classNames(styles.rightAlignedImage, styles.inlineWithText)}><CMSImage className={styles.alignedImage} image={imageRef} altText={title} sizes="(min-width: 641px) 300px, 100vw" width={300} /></div>;
        default:
            return <CMSImage className={classnames(styles.fullWidthImage, styles.alignedImage)} image={imageRef} altText={title} sizes="(min-width: 641px) 1100px, 100vw" width={600} />;
    }
};

const InlineFile = ({ value }: InlineFileProps) => {
    const { fileRef } = value;
    return <div className={styles.fileDownload}><a href={fileRef.file?.url} download={fileRef.file?.originalFilename} target="_blank"><MdFileDownload className={styles.downloadIcon} /> Last ned &quot;{fileRef.title}&quot;</a></div>


};
//#endregion


//#region [Other]

const TYPES_FULL: Record<string, PortableTextTypeComponent> = {
    youtube: YouTube,
    inlineImage: InlineImage,
    inlineFile: InlineFile,
    table: Table,
    instagramEmbed: Instagram,
    tiktokEmbed: TikTok,
    twitterEmbed: Twitter,
    facebookEmbed: Facebook
};

function NoRender() { return null; };

const TYPES_NO_CUSTOMCOMPONENTS: Record<string, PortableTextTypeComponent> = {
    youtube: NoRender,
    inlineImage: NoRender,
    inlineFile: NoRender,
    table: NoRender,
    instagramEmbed: NoRender,
    tiktokEmbed: NoRender,
    twitterEmbed: NoRender,
    facebookEmbed: NoRender
};


interface IInternalLinkProps extends TypedObject {
    docType: "bygdekinoenFilmomtale" | "bygdekinoenNewsDocument";
    slug: Slug;
}

interface IMailtoProps extends TypedObject {
    mailto: string;
}

const MARKS_NO_LINKS = {
    internalLink: ({ children }: PortableTextMarkComponentProps<IInternalLinkProps>) => {
        return <span>{children}</span>;
    },
    mailTo: ({ children }: PortableTextMarkComponentProps<ArbitraryTypedObject>) => {
        return <span>{children}</span>;
    },
    link: ({ children }: PortableTextMarkComponentProps<ArbitraryTypedObject>) => {
        return <span>{children}</span>;
    },
};



const MARKS_LINKS = {
    internalLink: ({ children, value }: PortableTextMarkComponentProps<IInternalLinkProps>) => {
        let url: string | null = null;
        try {
            url = getDocumentUrlBySlug(value!.docType, value!.slug);
        }
        catch {
            console.error("Value", value);
        }
        if (!value?.slug || !url) {
            return <span>{children}</span>
        }
        return (
            <Link href={url}>
                {children}
            </Link>
        )
    },
    mailTo: ({ children, value }: PortableTextMarkComponentProps<IMailtoProps>) => {
        return (<Link href={`mailto:${value!.mailto}`}>
            {children}
        </Link>);
    }
};

function buildComponents(disableLinks: boolean = false, disableCustomComponents: boolean = false): PortableTextComponents {
    return {
        types: disableCustomComponents ? TYPES_NO_CUSTOMCOMPONENTS : TYPES_FULL,
        marks: disableLinks ? MARKS_NO_LINKS : MARKS_LINKS
    };
}
//#endregion

//#region [Props]
type RichTextProps = {
    value?: (PortableTextBlock | ArbitraryTypedObject)[],
    className?: string,
    disableLinks?: boolean; // for use when the rich text is itself with a link
    disableCustomComponents?: boolean; // for use when images should not be allowed
}
//#endregion

//#region [Component]
const RichText = ({ value, className, disableLinks = false, disableCustomComponents = false }: RichTextProps) => {
    if (!value || value.length === 0) {
        return null;
    }
    const components = buildComponents(disableLinks, disableCustomComponents);
    return <div className={classnames(styles.richText, className)}><PortableText value={value} components={components} /></div>
}

export default RichText;
//#endregion
